import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  ShoppingCart,
  LocalShipping,
  Business,
  AttachMoney,
  TrendingUp,
  Pending,
  Campaign,
  AssignmentTurnedIn,
  DirectionsCar,
  CheckCircle,
} from '@mui/icons-material';
import { ordersAPI } from '../services/api';
import { io } from 'socket.io-client';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';

function Dashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadDashboard();

    // Socket.io 연결
    const socket = io(API_URL);
    socket.emit('join-admin');

    socket.on('order-updated', () => {
      loadDashboard();
    });

    return () => {
      socket.disconnect();
    };
  }, []);

  const loadDashboard = async () => {
    try {
      setLoading(true);
      const response = await ordersAPI.getDashboardStats();
      setStats(response.data.data);
      setError(null);
    } catch (err) {
      setError('데이터를 불러오는데 실패했습니다.');
      console.error('Dashboard error:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatPrice = (price) => {
    return Math.floor(price || 0).toLocaleString() + '원';
  };

  const getStatusChip = (status) => {
    const statusMap = {
      pending_price: { label: '금액검토', color: 'default' },
      price_proposed: { label: '입금대기', color: 'warning' },
      deferred_payment: { label: '추후입금', color: 'secondary' },
      before_dispatch: { label: '배차전', color: 'default' },
      dispatch_ready: { label: '배차준비', color: 'info' },
      dispatch_failed: { label: '배차실패', color: 'error' },
      pending: { label: '배차대기', color: 'info' },
      broadcasting: { label: '호출중', color: 'warning' },
      assigned: { label: '배차완료', color: 'primary' },
      in_progress: { label: '배송중', color: 'primary' },
      completed: { label: '완료', color: 'success' },
      cancelled: { label: '취소', color: 'error' },
    };
    const info = statusMap[status] || { label: status, color: 'default' };
    return <Chip label={info.label} color={info.color} size="small" />;
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  const { total, today, drivers, clients, monthly, recentOrders } = stats || {};

  return (
    <Box>
      <Typography variant="h5" fontWeight={600} gutterBottom>
        대시보드
      </Typography>

      {/* 오늘 통계 */}
      <Typography variant="subtitle1" color="textSecondary" sx={{ mb: 2 }}>
        오늘의 현황
      </Typography>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card sx={{ bgcolor: '#e3f2fd', height: '100%' }}>
            <CardContent sx={{ height: '100%', display: 'flex', alignItems: 'center' }}>
              <Box display="flex" justifyContent="space-between" alignItems="center" width="100%">
                <Box>
                  <Typography color="textSecondary" variant="body2">오늘 주문</Typography>
                  <Typography variant="h4" fontWeight={700} color="primary">
                    {today?.today_orders || 0}
                  </Typography>
                </Box>
                <ShoppingCart sx={{ fontSize: 40, color: '#1976d2', opacity: 0.7 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card sx={{ bgcolor: '#e8f5e9', height: '100%' }}>
            <CardContent sx={{ height: '100%', display: 'flex', alignItems: 'center' }}>
              <Box display="flex" justifyContent="space-between" alignItems="center" width="100%">
                <Box>
                  <Typography color="textSecondary" variant="body2">오늘 완료</Typography>
                  <Typography variant="h4" fontWeight={700} color="success.main">
                    {today?.today_completed || 0}
                  </Typography>
                </Box>
                <CheckCircle sx={{ fontSize: 40, color: '#4caf50', opacity: 0.7 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card sx={{ bgcolor: '#fff3e0', height: '100%' }}>
            <CardContent sx={{ height: '100%', display: 'flex', alignItems: 'center' }}>
              <Box display="flex" justifyContent="space-between" alignItems="center" width="100%">
                <Box>
                  <Typography color="textSecondary" variant="body2">오늘 매출</Typography>
                  <Typography variant="h4" fontWeight={700} color="warning.dark">
                    {formatPrice(today?.today_revenue)}
                  </Typography>
                </Box>
                <AttachMoney sx={{ fontSize: 40, color: '#ed6c02', opacity: 0.7 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card sx={{ bgcolor: '#e0f2f1', height: '100%' }}>
            <CardContent sx={{ height: '100%', display: 'flex', alignItems: 'center' }}>
              <Box display="flex" justifyContent="space-between" alignItems="center" width="100%">
                <Box>
                  <Typography color="textSecondary" variant="body2">오늘 수익</Typography>
                  <Typography variant="h4" fontWeight={700}
                    sx={{ color: Number(today?.today_profit) >= 0 ? '#00695c' : '#d32f2f' }}>
                    {today?.today_profit != null
                      ? `${Number(today.today_profit) >= 0 ? '+' : ''}${Number(today.today_profit).toLocaleString()}원`
                      : '-'}
                  </Typography>
                </Box>
                <TrendingUp sx={{ fontSize: 40, color: '#00897b', opacity: 0.7 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card sx={{ bgcolor: '#fce4ec', height: '100%' }}>
            <CardContent sx={{ height: '100%', display: 'flex', alignItems: 'center' }}>
              <Box display="flex" justifyContent="space-between" alignItems="center" width="100%">
                <Box>
                  <Typography color="textSecondary" variant="body2">활성 운전자</Typography>
                  <Typography variant="h4" fontWeight={700} color="secondary">
                    {drivers?.active_drivers || 0}
                  </Typography>
                </Box>
                <LocalShipping sx={{ fontSize: 40, color: '#d81b60', opacity: 0.7 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* 주문 상태별 통계 */}
      <Typography variant="subtitle1" color="textSecondary" sx={{ mb: 2 }}>
        주문 상태별 현황
      </Typography>
      <Grid container spacing={2} columns={{ xs: 12, sm: 12, md: 14 }} sx={{ mb: 3 }}>
        <Grid item xs={6} sm={4} md={2}>
          <Card>
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <Pending sx={{ fontSize: 30, color: '#9e9e9e', mb: 1 }} />
              <Typography variant="h5" fontWeight={700}>{total?.pending_price_orders || 0}</Typography>
              <Typography variant="body2" color="textSecondary">금액검토</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <Card>
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <AttachMoney sx={{ fontSize: 30, color: '#ff9800', mb: 1 }} />
              <Typography variant="h5" fontWeight={700}>{total?.price_proposed_orders || 0}</Typography>
              <Typography variant="body2" color="textSecondary">입금대기</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <Card>
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <Campaign sx={{ fontSize: 30, color: '#2196f3', mb: 1 }} />
              <Typography variant="h5" fontWeight={700}>{total?.pending_orders || 0}</Typography>
              <Typography variant="body2" color="textSecondary">배차대기</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <Card>
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <AssignmentTurnedIn sx={{ fontSize: 30, color: '#2196f3', mb: 1 }} />
              <Typography variant="h5" fontWeight={700}>{total?.assigned_orders || 0}</Typography>
              <Typography variant="body2" color="textSecondary">배차완료</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <Card>
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <DirectionsCar sx={{ fontSize: 30, color: '#673ab7', mb: 1 }} />
              <Typography variant="h5" fontWeight={700}>{total?.in_progress_orders || 0}</Typography>
              <Typography variant="body2" color="textSecondary">배송중</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <Card>
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <CheckCircle sx={{ fontSize: 30, color: '#4caf50', mb: 1 }} />
              <Typography variant="h5" fontWeight={700}>{total?.completed_orders || 0}</Typography>
              <Typography variant="body2" color="textSecondary">완료</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <Card>
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <Business sx={{ fontSize: 30, color: '#607d8b', mb: 1 }} />
              <Typography variant="h5" fontWeight={700}>{clients?.total_clients || 0}</Typography>
              <Typography variant="body2" color="textSecondary">거래업체</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* 월별 실적 (최근 6개월, 접수일 기준) */}
      {monthly && monthly.length > 0 && (
        <>
          <Typography variant="subtitle1" color="textSecondary" sx={{ mb: 2 }}>
            월별 실적 (최근 6개월)
          </Typography>
          <TableContainer component={Paper} sx={{ mb: 3 }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                  <TableCell>월</TableCell>
                  <TableCell align="right">주문</TableCell>
                  <TableCell align="right">매출</TableCell>
                  <TableCell align="right">수익</TableCell>
                  <TableCell sx={{ width: '35%' }}></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(() => {
                  const maxProfit = Math.max(...monthly.map((m) => Math.abs(Number(m.profit) || 0)), 1);
                  return monthly.map((m) => {
                    const profit = Number(m.profit) || 0;
                    const [y, mo] = m.ym.split('-');
                    return (
                      <TableRow key={m.ym} hover>
                        <TableCell sx={{ fontWeight: 600 }}>{y}년 {parseInt(mo, 10)}월</TableCell>
                        <TableCell align="right">{m.order_count}건</TableCell>
                        <TableCell align="right">{Number(m.revenue || 0).toLocaleString()}원</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700, color: profit >= 0 ? '#2e7d32' : '#d32f2f' }}>
                          {profit >= 0 ? '+' : ''}{profit.toLocaleString()}원
                        </TableCell>
                        <TableCell>
                          <Box sx={{
                            height: 10, borderRadius: 5,
                            width: `${Math.round(Math.abs(profit) / maxProfit * 100)}%`,
                            minWidth: profit !== 0 ? 6 : 0,
                            bgcolor: profit >= 0 ? '#66bb6a' : '#ef5350',
                          }} />
                        </TableCell>
                      </TableRow>
                    );
                  });
                })()}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}

      {/* 최근 주문 */}
      <Typography variant="subtitle1" color="textSecondary" sx={{ mb: 2 }}>
        최근 주문
      </Typography>
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: '#f5f5f5' }}>
              <TableCell>주문번호</TableCell>
              <TableCell>업체명</TableCell>
              <TableCell>출발지</TableCell>
              <TableCell>도착지</TableCell>
              <TableCell align="right">배차금액</TableCell>
              <TableCell>운전자</TableCell>
              <TableCell>상태</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {recentOrders?.map((order) => (
              <TableRow key={order.order_id} hover>
                <TableCell>{order.order_number}</TableCell>
                <TableCell>{order.company_name || '-'}</TableCell>
                <TableCell sx={{ maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {order.origin_address}
                </TableCell>
                <TableCell sx={{ maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {order.destination_address}
                </TableCell>
                <TableCell align="right">
                  {formatPrice(order.confirmed_price || order.broadcast_price)}
                </TableCell>
                <TableCell>{order.driver_name || '-'}</TableCell>
                <TableCell>{getStatusChip(order.status)}</TableCell>
              </TableRow>
            ))}
            {(!recentOrders || recentOrders.length === 0) && (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 3 }}>
                  최근 주문이 없습니다.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

export default Dashboard;
